import {inject, Injectable, Injector} from '@angular/core';
import {HttpBackend, HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {firstValueFrom} from 'rxjs';

import {AuthControllerService, BASE_PATH, Configuration} from '../../api';
import {AuthService} from './auth.service';

/** Schlüssel des Access-Tokens im localStorage – wie überall in der App. */
export const TOKEN_STORAGE_KEY = 'token';

/** Name des Web Locks, unter dem alle Tabs desselben Browsers nacheinander refreshen. */
export const TOKEN_REFRESH_LOCK = 'bic-token-refresh';

/** So lange muss ein Token mindestens noch gelten, sonst wird vorher erneuert. */
export const TOKEN_MIN_VALIDITY_SECONDS = 60;

/**
 * Hält das Access-Token frisch.
 *
 * <p>Das Access-Token (localStorage) lebt nur kurz. Erneuert wird es über `POST /auth/refresh`; das
 * Refresh-Token dazu liegt als httpOnly-Cookie `bic_refresh` im Browser und ist für JavaScript
 * unsichtbar. Das Backend rotiert es bei jedem Refresh.
 *
 * <p>Weil das Cookie für <em>alle</em> Tabs dasselbe ist, darf immer nur einer refreshen:
 * <ul>
 *   <li>im Tab über ein geteiltes Promise ({@link inFlight}),</li>
 *   <li>tabübergreifend über das Web Lock {@link TOKEN_REFRESH_LOCK}. Wer das Lock bekommt, liest
 *       das Token erneut – hat ein anderer Tab es inzwischen erneuert, wird nicht noch einmal
 *       refresht. Ein zweiter Refresh mit dem schon rotierten Cookie liefe sonst in das 401 des
 *       Backends.</li>
 * </ul>
 *
 * <p>Der Refresh läuft über einen eigenen {@link HttpClient} direkt auf dem {@link HttpBackend},
 * also an allen Interceptors vorbei: der {@link AuthInterceptor} würde sonst für den Refresh selbst
 * wieder ein Token holen wollen, und ein 401 hier ist kein Fehler für die globale Fehleranzeige.
 */
@Injectable({providedIn: 'root'})
export class TokenRefreshService {
  // Eigene Instanz statt der aus dem DI: es wird nur das übergebene Token geprüft, der tokenGetter
  // aus JWT_OPTIONS spielt keine Rolle. So hängt nicht jeder SseService-Nutzer an JWT_OPTIONS.
  private readonly jwtHelper = new JwtHelperService();
  // Alles Weitere erst bei Bedarf holen: der AuthInterceptor hängt an diesem Service, und AuthService
  // zieht über StateConfigService viele HttpClient-Services nach sich.
  private readonly injector = inject(Injector);
  private authApi: AuthControllerService | null = null;

  private inFlight: Promise<string | null> | null = null;

  /**
   * Liefert ein Access-Token, das noch mindestens {@link TOKEN_MIN_VALIDITY_SECONDS} gilt, und
   * erneuert es dafür bei Bedarf.
   *
   * @returns das Token; `null`, wenn niemand angemeldet ist oder die Sitzung abgelaufen ist (dann
   *          ist bereits abgemeldet und nach `/login` navigiert).
   * @throws der Fehler des Refresh-Requests, wenn das Backend nicht erreichbar ist oder mit 5xx
   *         antwortet. Dann bleibt der Nutzer angemeldet – ein Deployment soll niemanden abmelden.
   */
  getValidToken(): Promise<string | null> {
    const token = this.readToken();
    if (!token) {
      return Promise.resolve(null);
    }
    if (!this.expiresWithin(token, TOKEN_MIN_VALIDITY_SECONDS)) {
      return Promise.resolve(token);
    }
    return this.refresh(token);
  }

  /**
   * Erneuert nach einem 401 auf `rejectedToken`.
   *
   * <p>Steht inzwischen ein anderes, gültiges Token im localStorage (ein paralleler Refresh im
   * selben oder in einem anderen Tab), wird das genommen statt erneut zu refreshen.
   *
   * @returns wie {@link getValidToken}.
   */
  refreshAfterUnauthorized(rejectedToken: string): Promise<string | null> {
    const current = this.readToken();
    if (!current) {
      return Promise.resolve(null);
    }
    if (current !== rejectedToken && !this.expiresWithin(current, 0)) {
      return Promise.resolve(current);
    }
    return this.refresh(rejectedToken);
  }

  private refresh(staleToken: string): Promise<string | null> {
    if (!this.inFlight) {
      this.inFlight = this.refreshAcrossTabs(staleToken).finally(() => {
        this.inFlight = null;
      });
    }
    return this.inFlight;
  }

  private refreshAcrossTabs(staleToken: string): Promise<string | null> {
    // Web Locks gibt es in allen aktuellen Browsern, aber nur im sicheren Kontext (https,
    // localhost). Ohne sie bleibt es beim Single-Flight im Tab; das Backend fängt den seltenen
    // Doppel-Refresh zweier Tabs mit seinem Gnadenfenster ab (401 ohne Widerruf, siehe unten).
    if (!navigator.locks) {
      return this.refreshUnderLock(staleToken);
    }
    return navigator.locks.request(TOKEN_REFRESH_LOCK, () => this.refreshUnderLock(staleToken));
  }

  private async refreshUnderLock(staleToken: string): Promise<string | null> {
    const current = this.readToken();
    if (!current) {
      // Ein anderer Tab hat abgemeldet. Dessen Storage-Event meldet auch diesen Tab ab
      // (app.component.ts), hier ist nichts mehr zu tun.
      return null;
    }
    if (current !== staleToken && !this.expiresWithin(current, TOKEN_MIN_VALIDITY_SECONDS)) {
      return current;
    }

    let token: string | undefined;
    try {
      token = (await firstValueFrom(this.refreshApi().refresh())).token;
    } catch (error) {
      if (!(error instanceof HttpErrorResponse) || (error.status !== 401 && error.status !== 403)) {
        console.error('Token-Refresh fehlgeschlagen, die Sitzung bleibt bestehen:', error);
        throw error;
      }
      // Hat ein anderer Tab im selben Moment rotiert, antwortet das Backend 401, ohne die Sitzung zu
      // widerrufen – dessen neues Token steht dann schon im localStorage.
      const renewed = this.readToken();
      if (renewed && renewed !== current && !this.expiresWithin(renewed, 0)) {
        return renewed;
      }
      return this.endSession();
    }

    if (!token) {
      console.error('Token-Refresh: Antwort ohne Access-Token');
      return this.endSession();
    }
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    return token;
  }

  private refreshApi(): AuthControllerService {
    if (!this.authApi) {
      this.authApi = new AuthControllerService(
        new HttpClient(this.injector.get(HttpBackend)),
        this.injector.get(BASE_PATH, null, {optional: true}) as string,
        // Gleiche Konfiguration wie der App-weite Client, aber mit Cookies: ohne withCredentials
        // schickt der Browser `bic_refresh` nicht mit und verwirft das neue Set-Cookie der Antwort.
        new Configuration({...this.injector.get(Configuration, null, {optional: true}), withCredentials: true})
      );
    }
    return this.authApi;
  }

  private endSession(): null {
    this.injector.get(AuthService).logout();
    void this.injector.get(Router).navigate(['/login']);
    return null;
  }

  private readToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || null;
  }

  /** `true`, wenn das Token in weniger als `seconds` Sekunden abläuft – oder gar kein JWT ist. */
  private expiresWithin(token: string, seconds: number): boolean {
    try {
      return this.jwtHelper.isTokenExpired(token, seconds);
    } catch (error) {
      // decodeToken wirft bei allem, was kein JWT ist. Mit so einem Token kann kein Request
      // gelingen; der Refresh über das Cookie ist der einzige Weg zu einem brauchbaren.
      console.warn('Access-Token im localStorage ist kein gültiges JWT, es wird erneuert:', error);
      return true;
    }
  }
}
